import { CONVERSION_TABLE } from '@constants/conversion';
import { BODY_HEIGHT } from '@constants/fhir';

import type { ObservationRecord } from '@models/observation';

/**
 * Body height normalisation.
 *
 * Height reaches us in two FHIR shapes (see `ObservationRecord.setComponents`):
 * a single `valueQuantity` in metres, or a `component` pair holding feet and
 * inches separately. Everything that calculates with height - first/last,
 * high/low, change - must work from one unrounded number in one unit, so these
 * helpers collapse both shapes into metres and convert to the display unit only
 * at the presentation boundary.
 */

const [, , metresToInches, inchesToMetres] = CONVERSION_TABLE[BODY_HEIGHT];

const INCHES_PER_FOOT = 12;

/** The only units we can read as a height. Anything else fails safe to null. */
type HeightUnit = 'in' | 'ft' | 'm';

type HeightPart = { unit: HeightUnit; value: number };

function isHeightUnit(unit: string | null | undefined): unit is HeightUnit {
  return unit === 'm' || unit === 'ft' || unit === 'in';
}

function partToMetres({ unit, value }: HeightPart): number {
  switch (unit) {
    case 'm':
      return value;
    case 'ft':
      return inchesToMetres(value * INCHES_PER_FOOT);
    case 'in':
    default:
      return inchesToMetres(value);
  }
}

/**
 * Combine height components. Units drive the maths, never position, so
 * `[ft, in]` and `[in, ft]` give the same answer. Combinations we cannot read
 * unambiguously - a repeated unit, or metres alongside feet/inches - return
 * null rather than a plausible-looking wrong height.
 */
function combineParts(parts: HeightPart[]): number | null {
  if (!parts.length) {
    return null;
  }
  const units = parts.map(({ unit }) => unit);
  if (new Set(units).size !== units.length) {
    return null;
  }
  if (units.includes('m')) {
    return units.length === 1 ? parts[0].value : null;
  }
  return parts.reduce((total, part) => total + partToMetres(part), 0);
}

/**
 * Canonical body height in unrounded metres, or null when the record's units
 * are not a height we can interpret.
 *
 * Both storage shapes are validated the same way - a `cm` or UCUM-coded height
 * is rejected rather than read as if it were already metres.
 */
export function getHeightMetres(
  record: Pick<ObservationRecord, 'component' | 'unit' | 'value'>
): number | null {
  const { component, unit, value } = record;

  if (component?.length) {
    const parts: HeightPart[] = [];
    for (const entry of component) {
      const quantity = entry?.valueQuantity;
      const partUnit = quantity?.unit;
      const partValue = quantity?.value;
      if (typeof partValue !== 'number' || !isHeightUnit(partUnit)) {
        return null;
      }
      parts.push({ unit: partUnit, value: partValue });
    }
    return combineParts(parts);
  }

  if (typeof value !== 'number' || !isHeightUnit(unit)) {
    return null;
  }
  return partToMetres({ unit, value });
}

/**
 * Canonical metres to the active display unit, unrounded: metres in SI, total
 * inches in CON. Rounding belongs to `VITAL_SIGN_TYPES[BODY_HEIGHT].format`.
 */
export function toDisplayHeight(metres: number, system: string): number {
  return system === 'CON' ? metresToInches(metres) : metres;
}
